import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { formatApiError } from '../../api';
import PageHeader from '../../components/PageHeader';
import StatusPill, { STATUS_ORDER, STATUS_LABELS } from '../../components/StatusPill';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { ArrowLeft, Plus, Mail, Phone, Trash2, Save, FileText, FileSignature, Upload, FileDown, File as FileIcon, Receipt, Eye, EyeOff, Pencil, ShieldCheck, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { PROPOSAL_STATUS, GBP } from '../proposals/constants';
import { INVOICE_STATUS } from '../invoices/constants';
import { UploadDialog } from '../Documents';
import { API_BASE } from '../../api';
import { StatusChip as NDAStatusChip } from '../ndas/NDAsList';
import ClientProfitabilityTab from './ClientProfitabilityTab';
import { EmailStatusBadge, ContactStatusBadge, CONTACT_STATUS_LABELS, CONTACT_STATUS_ORDER } from '../leadgen/ContactBadges';

export default function ClientDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [client, setClient] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [activity, setActivity] = useState([]);
    const [saving, setSaving] = useState(false);
    const [promoting, setPromoting] = useState(false);
    const [tab, setTab] = useState('overview');
    const [newContactOpen, setNewContactOpen] = useState(false);

    const load = useCallback(async () => {
        try {
            const [c, ctc, act] = await Promise.all([
                api.get(`/clients/${id}`),
                api.get(`/clients/${id}/contacts`),
                api.get('/activity', { params: { client_id: id } }),
            ]);
            setClient(c.data);
            setContacts(ctc.data.items);
            setActivity(act.data.items);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || e.message);
            if (e?.response?.status === 404) navigate('/clients');
        }
    }, [id, navigate]);

    useEffect(() => { load(); }, [load]);

    const patch = async (changes) => {
        setSaving(true);
        try {
            const r = await api.patch(`/clients/${id}`, changes);
            setClient(r.data);
            toast.success('Saved');
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || e.message);
        } finally { setSaving(false); }
    };

    const promote = async () => {
        setPromoting(true);
        try {
            const r = await api.post(`/clients/${id}/promote`);
            setClient(r.data);
            toast.success('Promoted to Active Lead — now in the sales pipeline');
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || e.message);
        } finally { setPromoting(false); }
    };

    if (!client) return <div className="p-8 text-[color:var(--bm-text-muted)]">Loading…</div>;

    return (
        <div data-testid="client-detail-page" className="bm-fade-in">
            <PageHeader
                title={client.company_name}
                description={client.trading_name || ''}
                actions={
                    <div className="flex items-center gap-2">
                        {client.status === 'lead' && (
                            <Button onClick={promote} disabled={promoting} style={{ background: 'var(--bm-primary)' }} className="h-9" data-testid="client-promote-btn">
                                <TrendingUp size={15} className="mr-1.5" /> {promoting ? 'Promoting…' : 'Promote to Active Lead'}
                            </Button>
                        )}
                        <Button variant="outline" onClick={() => navigate('/clients')} className="h-9" data-testid="client-back-btn">
                            <ArrowLeft size={15} className="mr-1" /> Back
                        </Button>
                    </div>
                }
            >
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                    <StatusPill status={client.status} />
                    {client.fit && <span className="text-[11px] font-semibold uppercase tracking-wide px-2 py-1 rounded border bg-teal-50 text-teal-700 border-teal-200">Fit: {client.fit}</span>}
                    {client.email && <span className="text-[12.5px] text-[color:var(--bm-text-muted)] flex items-center gap-1"><Mail size={13} />{client.email}</span>}
                    {client.phone && <span className="text-[12.5px] text-[color:var(--bm-text-muted)] flex items-center gap-1"><Phone size={13} />{client.phone}</span>}
                    {client.owner_name && <span className="text-[12.5px] text-[color:var(--bm-text-subtle)]">Owner: {client.owner_name}</span>}
                </div>
            </PageHeader>

            <div className="px-8 py-6">
                <Tabs value={tab} onValueChange={setTab}>
                    <TabsList className="bg-white border border-[color:var(--bm-border)] rounded-lg p-1">
                        <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
                        <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts <span className="ml-1.5 text-[11px] text-[color:var(--bm-text-subtle)]">{contacts.length}</span></TabsTrigger>
                        <TabsTrigger value="proposals" data-testid="tab-proposals">Proposals</TabsTrigger>
                        <TabsTrigger value="contracts" data-testid="tab-contracts">Contracts</TabsTrigger>
                        <TabsTrigger value="ndas" data-testid="tab-ndas">NDAs</TabsTrigger>
                        <TabsTrigger value="modules" data-testid="tab-modules">Modules</TabsTrigger>
                        <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices / Ledger</TabsTrigger>
                        <TabsTrigger value="profitability" data-testid="tab-profitability">Profitability</TabsTrigger>
                        <TabsTrigger value="delivery" data-testid="tab-delivery">Delivery</TabsTrigger>
                        <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
                        <TabsTrigger value="email-activity" data-testid="tab-email-activity">Email Activity</TabsTrigger>
                        <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
                    </TabsList>

                    <TabsContent value="overview" className="mt-5">
                        <OverviewTab client={client} onSave={patch} saving={saving} />
                    </TabsContent>

                    <TabsContent value="contacts" className="mt-5">
                        <ContactsTab
                            contacts={contacts}
                            clientId={id}
                            onChange={load}
                            onNew={() => setNewContactOpen(true)}
                        />
                        <NewContactDialog
                            open={newContactOpen}
                            onClose={() => setNewContactOpen(false)}
                            clientId={id}
                            onCreated={() => { setNewContactOpen(false); load(); }}
                        />
                    </TabsContent>

                    <TabsContent value="proposals" className="mt-5"><ProposalsTabContent clientId={id} /></TabsContent>
                    <TabsContent value="contracts" className="mt-5"><PhaseStub title="Contracts" desc="Phase 3 — contracts are auto-created on proposal acceptance. Signed PDF + audit trail." /></TabsContent>
                    <TabsContent value="ndas" className="mt-5"><ClientNDAsTab clientId={id} /></TabsContent>
                    <TabsContent value="modules" className="mt-5"><PhaseStub title="Modules" desc="Modules will be visible once the client has an accepted proposal or active contract." /></TabsContent>
                    <TabsContent value="invoices" className="mt-5"><ClientLedgerTab clientId={id} /></TabsContent>
                    <TabsContent value="profitability" className="mt-5"><ClientProfitabilityTab clientId={id} /></TabsContent>
                    <TabsContent value="delivery" className="mt-5"><DeliveryTab client={client} onSave={patch} saving={saving} /></TabsContent>
                    <TabsContent value="documents" className="mt-5"><ClientDocumentsTab clientId={id} /></TabsContent>
                    <TabsContent value="email-activity" className="mt-5"><EmailActivityTab clientId={id} /></TabsContent>

                    <TabsContent value="activity" className="mt-5">
                        <ActivityTab items={activity} />
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}

function OverviewTab({ client, onSave, saving }) {
    const [form, setForm] = useState({ ...client });
    const [platforms, setPlatforms] = useState([]);
    useEffect(() => { setForm({ ...client }); }, [client]);
    useEffect(() => {
        api.get('/support/platforms').then(r => setPlatforms(r.data.items)).catch(() => {});
    }, []);

    const diff = Object.keys(form).reduce((acc, k) => {
        if (['id', 'created_at', 'updated_at', 'deleted_at', 'owner_user_id'].includes(k)) return acc;
        if (JSON.stringify(form[k]) !== JSON.stringify(client[k])) acc[k] = form[k];
        return acc;
    }, {});
    const hasChanges = Object.keys(diff).length > 0;

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 bg-white rounded-xl border border-[color:var(--bm-border)] p-6">
                <div className="flex items-center justify-between mb-5">
                    <div className="font-display text-[16px] font-semibold">Company details</div>
                    {hasChanges && (
                        <Button size="sm" disabled={saving} onClick={() => onSave(diff)} style={{ background: 'var(--bm-primary)' }} data-testid="client-save-btn">
                            <Save size={14} className="mr-1" /> Save changes
                        </Button>
                    )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Company name"  value={form.company_name || ''}  onChange={(v) => setForm({ ...form, company_name: v })} testId="overview-company-name" />
                    <Field label="Trading name"  value={form.trading_name || ''}  onChange={(v) => setForm({ ...form, trading_name: v })} />
                    <Field label="Main contact"  value={form.main_contact_name || ''} onChange={(v) => setForm({ ...form, main_contact_name: v })} />
                    <Field label="Email"         value={form.email || ''}         onChange={(v) => setForm({ ...form, email: v })} type="email" />
                    <Field label="Phone"         value={form.phone || ''}         onChange={(v) => setForm({ ...form, phone: v })} />
                    <Field label="Website"       value={form.website || ''}       onChange={(v) => setForm({ ...form, website: v })} />
                    <Field label="Location"      value={form.location || ''}      onChange={(v) => setForm({ ...form, location: v })} testId="overview-location" />
                    <Field label="Industry"      value={form.industry || ''}      onChange={(v) => setForm({ ...form, industry: v })} />
                    <Field label="Company size"  value={form.company_size || ''}  onChange={(v) => setForm({ ...form, company_size: v })} />
                    <Field label="Lead source"   value={form.source || ''}        onChange={(v) => setForm({ ...form, source: v })} />
                    <Field label="Fit"           value={form.fit || ''}           onChange={(v) => setForm({ ...form, fit: v })} testId="overview-fit" />
                    <Field label="Campaign"      value={form.campaign_name || ''} onChange={(v) => setForm({ ...form, campaign_name: v })} testId="overview-campaign-name" />
                    <div>
                        <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Support Hub platform</Label>
                        <Select value={form.platform || '__none__'} onValueChange={(v) => setForm({ ...form, platform: v === '__none__' ? null : v })}>
                            <SelectTrigger className="mt-1.5 h-10" data-testid="overview-platform-select"><SelectValue placeholder="None" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__none__">None</SelectItem>
                                {platforms.map(p => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Status</Label>
                        <Select value={form.status || 'lead'} onValueChange={(v) => setForm({ ...form, status: v })}>
                            <SelectTrigger className="mt-1.5 h-10" data-testid="overview-status-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {STATUS_ORDER.map(s => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="mt-5">
                    <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Prospect description / technology signal</Label>
                    <Textarea
                        value={form.description || ''}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        placeholder="What this company does, and any tech signal (e.g. has a client portal, uses X software)…"
                        className="mt-1.5 min-h-[70px]"
                        data-testid="overview-description"
                    />
                    {form.tech_signal && <div className="mt-1.5 text-[12px] text-[color:var(--bm-text-muted)]"><span className="font-medium">Tech signal:</span> {form.tech_signal}</div>}
                </div>
                <div className="mt-5">
                    <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Internal notes</Label>
                    <Textarea
                        value={form.notes || ''}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        placeholder="Add private notes about this client…"
                        className="mt-1.5 min-h-[100px]"
                        data-testid="overview-notes"
                    />
                </div>
            </div>

            <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6">
                <div className="font-display text-[16px] font-semibold mb-4">Billing address</div>
                <BillingAddress
                    addr={form.billing_address || {}}
                    onChange={(a) => setForm({ ...form, billing_address: a })}
                />
                <div className="mt-3">
                    <Field label="VAT number" value={form.vat_number || ''}
                           onChange={(v) => setForm({ ...form, vat_number: v })}
                           testId="overview-vat-number" />
                    <p className="mt-1 text-[11px] text-[color:var(--bm-text-subtle)]">Printed on this client's invoices when set.</p>
                </div>

                <div className="mt-6 pt-5 border-t border-[color:var(--bm-border)]">
                    <div className="font-display text-[15px] font-semibold mb-1">Beyond Operations link</div>
                    <div className="text-[11.5px] text-[color:var(--bm-text-subtle)] mb-3">Optional metadata. Activation comes in Phase 5.</div>
                    <Field label="Tenant slug" value={form?.tenant_link?.tenant_slug || ''}
                           onChange={(v) => setForm({ ...form, tenant_link: { ...(form.tenant_link || {}), tenant_slug: v } })} />
                    <Field label="Domain" value={form?.tenant_link?.domain || ''}
                           onChange={(v) => setForm({ ...form, tenant_link: { ...(form.tenant_link || {}), domain: v } })} />
                </div>
            </div>
        </div>
    );
}

function Field({ label, value, onChange, type = 'text', testId }) {
    return (
        <div>
            <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">{label}</Label>
            <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1.5 h-10" data-testid={testId} />
        </div>
    );
}

function BillingAddress({ addr, onChange }) {
    const up = (k, v) => onChange({ ...addr, [k]: v });
    return (
        <div className="space-y-3">
            <Field label="Address line 1" value={addr.line1 || ''} onChange={(v) => up('line1', v)} />
            <Field label="Address line 2" value={addr.line2 || ''} onChange={(v) => up('line2', v)} />
            <div className="grid grid-cols-2 gap-3">
                <Field label="City" value={addr.city || ''} onChange={(v) => up('city', v)} />
                <Field label="Postcode" value={addr.postcode || ''} onChange={(v) => up('postcode', v)} />
            </div>
            <Field label="Country" value={addr.country || 'United Kingdom'} onChange={(v) => up('country', v)} />
        </div>
    );
}

function ContactsTab({ contacts, clientId, onChange, onNew }) {
    const del = async (cid) => {
        if (!confirm('Delete this contact?')) return;
        try {
            await api.delete(`/clients/${clientId}/contacts/${cid}`);
            toast.success('Contact removed');
            onChange();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };

    const toggleApprover = async (c) => {
        try {
            await api.patch(`/clients/${clientId}/contacts/${c.id}`, { is_support_approver: !c.is_support_approver });
            toast.success(c.is_support_approver ? 'Removed as Support Hub approver' : 'Set as Support Hub approver');
            onChange();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };

    const setContactStatus = async (c, status) => {
        try {
            await api.patch(`/clients/${clientId}/contacts/${c.id}`, { contact_status: status });
            onChange();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };

    const toggleDNC = async (c) => {
        try {
            await api.patch(`/clients/${clientId}/contacts/${c.id}`, { do_not_contact: !c.do_not_contact, contact_status: !c.do_not_contact ? 'do_not_contact' : 'not_contacted' });
            toast.success(!c.do_not_contact ? 'Added to Do Not Contact' : 'Removed from Do Not Contact');
            onChange();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };

    return (
        <div className="bg-white rounded-xl border border-[color:var(--bm-border)] overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[color:var(--bm-border)]">
                <div className="font-display text-[15px] font-semibold">Contacts</div>
                <Button size="sm" onClick={onNew} style={{ background: 'var(--bm-primary)' }} data-testid="contacts-new-btn">
                    <Plus size={14} className="mr-1" /> Add contact
                </Button>
            </div>
            {contacts.length === 0 ? (
                <div className="px-5 py-10 text-center text-[color:var(--bm-text-muted)] text-[13px]">No contacts yet.</div>
            ) : (
                <table className="w-full text-[13px]">
                    <thead className="bg-[color:var(--bm-bg-mute)] text-[color:var(--bm-text-muted)]">
                        <tr>
                            <th className="text-left font-medium px-5 py-3">Name</th>
                            <th className="text-left font-medium px-4 py-3">Role</th>
                            <th className="text-left font-medium px-4 py-3">Email</th>
                            <th className="text-left font-medium px-4 py-3">Email quality</th>
                            <th className="text-left font-medium px-4 py-3">Outreach status</th>
                            <th className="text-left font-medium px-4 py-3">Support Hub</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {contacts.map(c => (
                            <tr key={c.id} className="border-t border-[color:var(--bm-border)]" data-testid={`contact-row-${c.id}`}>
                                <td className="px-5 py-3">
                                    {c.name}
                                    {c.is_primary && <span className="ml-2 text-[10.5px] font-semibold uppercase tracking-wide bg-[color:var(--bm-accent-50)] text-[color:var(--bm-accent-600)] px-1.5 py-0.5 rounded">Primary</span>}
                                    {c.lead_stage === 'Engaged' && <span className="ml-2 text-[10.5px] font-semibold uppercase tracking-wide bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">Engaged</span>}
                                </td>
                                <td className="px-4 py-3 text-[color:var(--bm-text-muted)]">{c.role || '—'}</td>
                                <td className="px-4 py-3 text-[color:var(--bm-text-muted)]">{c.email || '—'}</td>
                                <td className="px-4 py-3"><EmailStatusBadge status={c.email_status} confidence={c.confidence_percent} testId={`contact-email-status-${c.id}`} /></td>
                                <td className="px-4 py-3">
                                    <select value={c.contact_status || 'not_contacted'} onChange={(e) => setContactStatus(c, e.target.value)}
                                            className="text-[11.5px] rounded border border-[color:var(--bm-border)] px-1.5 py-1 bg-white" data-testid={`contact-status-select-${c.id}`}>
                                        {CONTACT_STATUS_ORDER.map(s => <option key={s} value={s}>{CONTACT_STATUS_LABELS[s]}</option>)}
                                    </select>
                                </td>
                                <td className="px-4 py-3">
                                    <button onClick={() => toggleApprover(c)}
                                            className={`text-[10.5px] font-semibold uppercase tracking-wide px-2 py-1 rounded border ${c.is_support_approver ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}
                                            data-testid={`contact-approver-toggle-${c.id}`}>
                                        {c.is_support_approver ? 'Approver ✓' : 'Set as approver'}
                                    </button>
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <div className="inline-flex items-center gap-1">
                                        <button onClick={() => toggleDNC(c)} title={c.do_not_contact ? 'Remove Do Not Contact' : 'Mark Do Not Contact'}
                                                className={`text-[10px] font-semibold uppercase px-1.5 py-1 rounded border ${c.do_not_contact ? 'bg-red-100 text-red-800 border-red-300' : 'text-[color:var(--bm-text-subtle)] border-transparent hover:border-[color:var(--bm-border)]'}`}
                                                data-testid={`contact-dnc-toggle-${c.id}`}>
                                            DNC
                                        </button>
                                        <button onClick={() => del(c.id)} className="text-[color:var(--bm-text-subtle)] hover:text-red-600 p-1"><Trash2 size={15} /></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            <div className="px-5 py-3 text-[11.5px] text-[color:var(--bm-text-subtle)] border-t border-[color:var(--bm-border)]">
                Support Hub approvers are notified to approve, decline or request more information on enhancement requests for this client.
            </div>
        </div>
    );
}

function NewContactDialog({ open, onClose, clientId, onCreated }) {
    const [form, setForm] = useState({ name: '', role: '', email: '', phone: '', is_primary: false, is_support_approver: false });
    const submit = async (e) => {
        e.preventDefault();
        try {
            const body = { ...form };
            if (!body.email) delete body.email;
            await api.post(`/clients/${clientId}/contacts`, body);
            toast.success('Contact added');
            setForm({ name: '', role: '', email: '', phone: '', is_primary: false, is_support_approver: false });
            onCreated();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-[460px]">
                <DialogHeader>
                    <DialogTitle>Add contact</DialogTitle>
                    <DialogDescription className="text-[12.5px] text-[color:var(--bm-text-muted)]">Add a person at this client.</DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3 mt-2">
                    <Field label="Name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                    <Field label="Role / Job title" value={form.role} onChange={(v) => setForm({ ...form, role: v })} />
                    <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} type="email" />
                    <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
                    <label className="flex items-center gap-2 text-[12.5px] text-[color:var(--bm-text-muted)]">
                        <input type="checkbox" checked={form.is_primary} onChange={(e) => setForm({ ...form, is_primary: e.target.checked })} />
                        Primary contact
                    </label>
                    <label className="flex items-center gap-2 text-[12.5px] text-[color:var(--bm-text-muted)]">
                        <input type="checkbox" checked={form.is_support_approver} onChange={(e) => setForm({ ...form, is_support_approver: e.target.checked })} data-testid="new-contact-approver-checkbox" />
                        Support Hub approver (receives enhancement approval requests)
                    </label>
                    <DialogFooter className="pt-2">
                        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                        <Button type="submit" style={{ background: 'var(--bm-primary)' }}>Add</Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function ActivityTab({ items }) {
    if (items.length === 0) return <div className="bg-white rounded-xl border border-[color:var(--bm-border)] px-5 py-10 text-center text-[color:var(--bm-text-muted)] text-[13px]">No activity yet.</div>;
    return (
        <div className="bg-white rounded-xl border border-[color:var(--bm-border)] divide-y divide-[color:var(--bm-border)]" data-testid="client-activity-list">
            {items.map(a => {
                const isView = a.entity_type === 'proposal' && a.action === 'viewed';
                const meta = a.meta || {};
                const dotClass = isView ? 'bg-blue-500' : 'bg-[color:var(--bm-accent)]';
                return (
                    <div key={a.id} className="px-5 py-3 flex items-start gap-3" data-testid={`activity-row-${a.id}`}>
                        <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${dotClass}`} />
                        <div className="flex-1 min-w-0">
                            <div className="text-[13px] text-[color:var(--bm-text)]">
                                <span className="font-medium">{a.actor_name || 'System'}</span>{' '}
                                <span className="text-[color:var(--bm-text-muted)]">
                                    {isView ? 'viewed' : a.action} {a.entity_type}
                                </span>
                                {isView && meta.proposal_number && (
                                    <span className="ml-1.5 text-[color:var(--bm-text-muted)]">
                                        · <span className="font-mono text-[12px]">{meta.proposal_number}</span>
                                        {meta.view_kind === 'narrative' && <span className="ml-1 text-[11px] uppercase tracking-wide bg-slate-100 px-1.5 py-0.5 rounded">narrative</span>}
                                        {meta.is_first_view && <span className="ml-1 text-[11px] uppercase tracking-wide bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">first view</span>}
                                    </span>
                                )}
                            </div>
                            <div className="text-[11.5px] text-[color:var(--bm-text-subtle)] mt-0.5">
                                {new Date(a.timestamp).toLocaleString('en-GB')}
                                {isView && meta.view_count && <span className="ml-2">· view #{meta.view_count}</span>}
                                {isView && meta.ip && <span className="ml-2 font-mono">· {meta.ip}</span>}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function EmailActivityTab({ clientId }) {
    const [items, setItems] = useState(null);
    useEffect(() => {
        api.get(`/leadgen/companies/${clientId}/email-activity`).then(r => setItems(r.data.items)).catch(() => setItems([]));
    }, [clientId]);

    const kindTone = { sent: 'bg-slate-50 text-slate-600 border-slate-200', reply: 'bg-emerald-50 text-emerald-700 border-emerald-200', bounce: 'bg-red-50 text-red-700 border-red-200', failed: 'bg-red-50 text-red-700 border-red-200', queued: 'bg-sky-50 text-sky-700 border-sky-200' };

    if (items === null) return <div className="p-6 text-[color:var(--bm-text-muted)] text-[13px]">Loading…</div>;
    if (items.length === 0) return <div className="bg-white rounded-xl border border-[color:var(--bm-border)] px-5 py-10 text-center text-[color:var(--bm-text-muted)] text-[13px]" data-testid="email-activity-empty">No campaign emails or replies yet for this company.</div>;

    return (
        <div className="bg-white rounded-xl border border-[color:var(--bm-border)] divide-y divide-[color:var(--bm-border)]" data-testid="email-activity-list">
            {items.map((it, i) => (
                <div key={i} className="px-5 py-3 flex items-start gap-3" data-testid={`email-activity-row-${i}`}>
                    <span className={`text-[9.5px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border shrink-0 mt-0.5 ${kindTone[it.kind] || kindTone.sent}`}>{it.kind}</span>
                    <div className="flex-1 min-w-0">
                        <div className="text-[13px]">
                            <span className="font-medium">{it.contact_name || '—'}</span>
                            {it.campaign_name && <span className="text-[color:var(--bm-text-muted)]"> · {it.campaign_name}</span>}
                        </div>
                        <div className="text-[12.5px] text-[color:var(--bm-text-muted)] mt-0.5">{it.subject || '(no subject)'}</div>
                        {it.body_preview && <div className="text-[12px] text-[color:var(--bm-text-subtle)] mt-0.5 truncate">{it.body_preview}</div>}
                        <div className="text-[11px] text-[color:var(--bm-text-subtle)] mt-0.5">{it.at ? new Date(it.at).toLocaleString('en-GB') : ''}</div>
                    </div>
                </div>
            ))}
        </div>
    );
}


function PhaseStub({ title, desc }) {
    return (
        <div className="bg-white rounded-xl border border-[color:var(--bm-border)] border-dashed px-5 py-10 text-center">
            <div className="font-display text-[16px] font-semibold text-[color:var(--bm-text)]">{title}</div>
            <div className="text-[12.5px] text-[color:var(--bm-text-muted)] mt-1.5 max-w-md mx-auto">{desc}</div>
        </div>
    );
}

function ClientLedgerTab({ clientId }) {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get(`/clients/${clientId}/ledger`);
            setData(r.data);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
        finally { setLoading(false); }
    }, [clientId]);
    useEffect(() => { load(); }, [load]);

    if (loading) return <div className="bg-white rounded-xl border border-[color:var(--bm-border)] px-5 py-8 text-center text-[color:var(--bm-text-muted)]">Loading ledger…</div>;
    if (!data) return null;

    const s = data.summary || {};
    const aged = data.aged_debtors || {};
    const hasActivity = (data.invoices || []).length > 0 || (data.payments || []).length > 0;

    return (
        <div className="space-y-5" data-testid="client-ledger-tab">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <SummaryCard label="Total invoiced" value={GBP(s.total_invoiced)} />
                <SummaryCard label="Total paid" value={GBP(s.total_paid)} accent="emerald" />
                <SummaryCard label="Outstanding" value={GBP(s.outstanding)} accent={s.outstanding > 0 ? 'amber' : 'muted'} />
                <SummaryCard label="Overdue" value={GBP(s.overdue)} accent={s.overdue > 0 ? 'red' : 'muted'} />
            </div>

            <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-5">
                <div className="flex items-center justify-between mb-3">
                    <div className="font-display text-[15px] font-semibold">Aged debtors</div>
                    {s.next_invoice_due && (
                        <div className="text-[11.5px] text-[color:var(--bm-text-muted)]">
                            Next due: <span className="font-medium text-[color:var(--bm-text)]">{s.next_invoice_due.invoice_number}</span> · {s.next_invoice_due.due_date} · {GBP(s.next_invoice_due.balance_due)}
                        </div>
                    )}
                </div>
                <div className="grid grid-cols-5 gap-2 text-[12.5px]">
                    {[
                        ['Current', 'current', 'slate'],
                        ['1–30d', 'd1_30', 'sky'],
                        ['31–60d', 'd31_60', 'amber'],
                        ['61–90d', 'd61_90', 'orange'],
                        ['90+d', 'd90_plus', 'red'],
                    ].map(([lbl, k, tone]) => (
                        <div key={k} className="rounded-lg border border-[color:var(--bm-border)] bg-[color:var(--bm-bg-mute)] p-3">
                            <div className={`text-[10.5px] uppercase tracking-wide font-semibold text-${tone}-700`}>{lbl}</div>
                            <div className="mt-1 font-display text-[15px] font-semibold">{GBP(aged[k] || 0)}</div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="bg-white rounded-xl border border-[color:var(--bm-border)] overflow-hidden">
                <div className="px-5 py-3 border-b border-[color:var(--bm-border)] font-display text-[15px] font-semibold">Invoices</div>
                {(data.invoices || []).length === 0 ? (
                    <div className="px-5 py-10 text-center text-[color:var(--bm-text-muted)] text-[13px]">
                        <Receipt size={28} className="mx-auto text-[color:var(--bm-text-subtle)]" />
                        <div className="mt-2">No invoices yet. Create them from an active contract.</div>
                    </div>
                ) : (
                    <table className="w-full text-[13px]">
                        <thead className="bg-[color:var(--bm-bg-mute)] text-[color:var(--bm-text-muted)]">
                            <tr>
                                <th className="text-left font-medium px-5 py-3">Invoice</th>
                                <th className="text-left font-medium px-4 py-3">Type</th>
                                <th className="text-left font-medium px-4 py-3">Status</th>
                                <th className="text-left font-medium px-4 py-3">Issue</th>
                                <th className="text-left font-medium px-4 py-3">Due</th>
                                <th className="text-right font-medium px-4 py-3">Total</th>
                                <th className="text-right font-medium px-4 py-3">Balance</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.invoices.map(i => (
                                <tr key={i.id} onClick={() => navigate(`/invoices/${i.id}`)}
                                    className="border-t border-[color:var(--bm-border)] hover:bg-[color:var(--bm-bg-mute)] cursor-pointer"
                                    data-testid={`client-invoice-row-${i.id}`}>
                                    <td className="px-5 py-3 font-medium">{i.invoice_number}</td>
                                    <td className="px-4 py-3 text-[color:var(--bm-text-muted)]">{(i.type || '').replace('_', ' ')}</td>
                                    <td className="px-4 py-3"><span className={`bm-pill ${INVOICE_STATUS[i.status]?.tone || ''} border`}>{INVOICE_STATUS[i.status]?.label || i.status}</span></td>
                                    <td className="px-4 py-3 text-[color:var(--bm-text-muted)]">{i.issue_date}</td>
                                    <td className="px-4 py-3 text-[color:var(--bm-text-muted)]">{i.due_date}</td>
                                    <td className="px-4 py-3 text-right">{GBP(i.total)}</td>
                                    <td className="px-4 py-3 text-right font-semibold">{GBP(i.balance_due)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {hasActivity && (
                <div className="bg-white rounded-xl border border-[color:var(--bm-border)] overflow-hidden">
                    <div className="px-5 py-3 border-b border-[color:var(--bm-border)] font-display text-[15px] font-semibold">Timeline</div>
                    <div className="divide-y divide-[color:var(--bm-border)]">
                        {(data.timeline || []).map(t => (
                            <div key={`${t.kind}-${t.id}`} className="px-5 py-2.5 flex items-center gap-3 text-[13px]" data-testid={`ledger-timeline-${t.kind}-${t.id}`}>
                                <span className={`inline-block w-1.5 h-1.5 rounded-full ${t.kind === 'payment' ? 'bg-emerald-500' : 'bg-[color:var(--bm-accent)]'}`} />
                                <span className="text-[color:var(--bm-text-muted)] w-[96px] shrink-0">{t.date}</span>
                                <span className="flex-1 truncate">
                                    <span className="font-medium">{t.ref || '—'}</span>
                                    <span className="text-[color:var(--bm-text-muted)] ml-2">{t.description}</span>
                                </span>
                                <span className={`font-medium tabular-nums ${t.amount < 0 ? 'text-emerald-600' : 'text-[color:var(--bm-text)]'}`}>{GBP(t.amount)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function SummaryCard({ label, value, accent }) {
    const toneMap = {
        emerald: 'text-emerald-700',
        amber: 'text-amber-700',
        red: 'text-red-700',
        muted: 'text-[color:var(--bm-text-muted)]',
    };
    return (
        <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-4">
            <div className="text-[10.5px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold">{label}</div>
            <div className={`mt-1 font-display text-[18px] font-semibold ${toneMap[accent] || 'text-[color:var(--bm-text)]'}`}>{value}</div>
        </div>
    );
}

function ClientNDAsTab({ clientId }) {
    const navigate = useNavigate();
    const [rows, setRows] = useState(null);
    const load = useCallback(() => {
        api.get(`/ndas`, { params: { client_id: clientId } })
            .then(r => setRows(r.data))
            .catch(() => setRows([]));
    }, [clientId]);
    useEffect(() => { load(); }, [load]);

    if (!rows) return <div className="text-sm text-[color:var(--bm-text-muted)]">Loading…</div>;

    return (
        <div className="space-y-3" data-testid="client-ndas-tab">
            <div className="flex items-center justify-between">
                <div className="text-[13px] text-[color:var(--bm-text-muted)]">
                    Non-Disclosure Agreements linked to this client. Signed PDFs also appear under <em>Documents</em>.
                </div>
                <Button
                    size="sm"
                    onClick={() => navigate(`/ndas?client_id=${clientId}`)}
                    data-testid="client-new-nda-btn"
                >
                    <Plus className="w-3.5 h-3.5 mr-1" /> New NDA
                </Button>
            </div>

            {rows.length === 0 ? (
                <div className="bg-white border border-[color:var(--bm-border)] rounded-lg p-8 text-center">
                    <ShieldCheck className="w-9 h-9 mx-auto text-slate-300 mb-2" />
                    <div className="text-sm text-slate-600">No NDAs yet for this client.</div>
                    <div className="text-[11.5px] text-slate-500 mt-1">
                        Create one to protect the IP and platform access you share.
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        className="mt-3"
                        onClick={() => navigate(`/ndas?client_id=${clientId}`)}
                    >
                        <Plus className="w-3.5 h-3.5 mr-1" /> Create the first NDA
                    </Button>
                </div>
            ) : (
                <div className="bg-white rounded-lg border border-[color:var(--bm-border)] overflow-hidden">
                    <table className="w-full text-[13px]">
                        <thead className="bg-slate-50 border-b border-[color:var(--bm-border)]">
                            <tr className="text-[11px] uppercase tracking-wide text-slate-500">
                                <th className="text-left font-semibold px-3 py-2.5">NDA #</th>
                                <th className="text-left font-semibold px-3 py-2.5">Recipient</th>
                                <th className="text-left font-semibold px-3 py-2.5">Status</th>
                                <th className="text-left font-semibold px-3 py-2.5">Signed</th>
                                <th className="px-3 py-2.5 w-12"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(n => (
                                <tr
                                    key={n.id}
                                    onClick={() => navigate(`/ndas/${n.id}`)}
                                    className="border-b border-[color:var(--bm-border)] hover:bg-slate-50 cursor-pointer"
                                    data-testid={`client-nda-row-${n.id}`}
                                >
                                    <td className="px-3 py-2.5 font-medium text-slate-800">{n.nda_number}</td>
                                    <td className="px-3 py-2.5">
                                        {n.recipient_name}
                                        <div className="text-[11px] text-slate-500">{n.recipient_email}</div>
                                    </td>
                                    <td className="px-3 py-2.5"><NDAStatusChip status={n.status} /></td>
                                    <td className="px-3 py-2.5 text-slate-600 text-[12px]">
                                        {n.counter_signed_at
                                            ? new Date(n.counter_signed_at).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
                                            : n.recipient_signed_at
                                                ? <span className="text-violet-600">awaiting counter-sign</span>
                                                : '—'}
                                    </td>
                                    <td className="px-3 py-2.5 text-right">
                                        {n.status === 'signed' && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    window.open(`${API_BASE}/ndas/${n.id}/pdf`, '_blank');
                                                }}
                                                className="p-1.5 text-slate-400 hover:text-[color:var(--bm-primary)]"
                                                title="Download signed PDF"
                                            >
                                                <FileDown className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}



function ClientDocumentsTab({ clientId }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploadOpen, setUploadOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get('/documents', { params: { client_id: clientId } });
            setItems(r.data.items);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
        finally { setLoading(false); }
    }, [clientId]);
    useEffect(() => { load(); }, [load]);

    const del = async (docId) => {
        if (!confirm('Delete this document?')) return;
        try { await api.delete(`/documents/${docId}`); toast.success('Deleted'); load(); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };

    const TYPE_LABEL = {
        proposal: 'Proposal', signed_contract: 'Signed Contract', invoice: 'Invoice',
        statement: 'Statement', meeting_notes: 'Meeting Notes', discovery: 'Discovery', scope: 'Scope', other: 'Other',
    };
    const humanSize = (n) => {
        if (!n && n !== 0) return '—';
        if (n < 1024) return `${n} B`;
        if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
        return `${(n / 1024 / 1024).toFixed(2)} MB`;
    };

    return (
        <div className="space-y-4" data-testid="client-documents-tab">
            <div className="bg-white rounded-xl border border-[color:var(--bm-border)] overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-[color:var(--bm-border)]">
                    <div className="font-display text-[15px] font-semibold">Documents</div>
                    <Button size="sm" onClick={() => setUploadOpen(true)} style={{ background: 'var(--bm-primary)' }} data-testid="client-documents-upload-btn">
                        <Upload size={14} className="mr-1" /> Upload
                    </Button>
                </div>
                {loading && <div className="px-5 py-10 text-center text-[color:var(--bm-text-muted)]">Loading…</div>}
                {!loading && items.length === 0 && (
                    <div className="px-5 py-12 text-center">
                        <FileIcon size={28} className="mx-auto text-[color:var(--bm-text-subtle)]" />
                        <div className="mt-2 text-[13px] text-[color:var(--bm-text-muted)]">No documents uploaded for this client yet.</div>
                    </div>
                )}
                {!loading && items.length > 0 && (
                    <table className="w-full text-[13px]">
                        <thead className="bg-[color:var(--bm-bg-mute)] text-[color:var(--bm-text-muted)]">
                            <tr>
                                <th className="text-left font-medium px-5 py-3">Filename</th>
                                <th className="text-left font-medium px-4 py-3">Type</th>
                                <th className="text-right font-medium px-4 py-3">Size</th>
                                <th className="text-left font-medium px-4 py-3">Uploaded</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(d => (
                                <tr key={d.id} className="border-t border-[color:var(--bm-border)]" data-testid={`client-document-row-${d.id}`}>
                                    <td className="px-5 py-3"><FileIcon size={14} className="inline mr-1.5 text-[color:var(--bm-text-subtle)]" />{d.filename}</td>
                                    <td className="px-4 py-3 text-[color:var(--bm-text-muted)]">{TYPE_LABEL[d.type] || d.type}</td>
                                    <td className="px-4 py-3 text-right text-[color:var(--bm-text-muted)]">{humanSize(d.size_bytes)}</td>
                                    <td className="px-4 py-3 text-[color:var(--bm-text-muted)]">{new Date(d.uploaded_at).toLocaleString('en-GB')}</td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="inline-flex gap-1">
                                            <a href={`${API_BASE}/documents/${d.id}/download`} className="p-1.5 text-[color:var(--bm-text-subtle)] hover:text-[color:var(--bm-primary)]" title="Download"><FileDown size={14} /></a>
                                            <button onClick={() => del(d.id)} className="p-1.5 text-[color:var(--bm-text-subtle)] hover:text-red-600" title="Delete"><Trash2 size={14} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            <UploadDialog
                open={uploadOpen}
                onClose={() => setUploadOpen(false)}
                clients={[]}
                preClientId={clientId}
                onUploaded={() => { setUploadOpen(false); load(); }}
            />
        </div>
    );
}

function ProposalsTabContent({ clientId }) {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = React.useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get('/proposals', { params: { client_id: clientId } });
            setItems(r.data.items);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setLoading(false); }
    }, [clientId]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="bg-white rounded-xl border border-[color:var(--bm-border)] overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[color:var(--bm-border)]">
                <div className="font-display text-[15px] font-semibold">Proposals</div>
                <Button size="sm" onClick={() => navigate(`/proposals?new=1&client_id=${clientId}`)} style={{ background: 'var(--bm-primary)' }} data-testid="client-new-proposal-btn">
                    <Plus size={14} className="mr-1" /> New proposal
                </Button>
            </div>
            {loading && <div className="px-5 py-8 text-center text-[color:var(--bm-text-muted)]">Loading…</div>}
            {!loading && items.length === 0 && (
                <div className="px-5 py-10 text-center">
                    <FileText size={32} className="mx-auto text-[color:var(--bm-text-subtle)]" />
                    <div className="mt-2 text-[13px] text-[color:var(--bm-text-muted)]">No proposals for this client yet.</div>
                </div>
            )}
            {items.length > 0 && (
                <table className="w-full text-[13px]">
                    <thead className="bg-[color:var(--bm-bg-mute)] text-[color:var(--bm-text-muted)]">
                        <tr>
                            <th className="text-left font-medium px-5 py-3">Proposal</th>
                            <th className="text-left font-medium px-4 py-3">Status</th>
                            <th className="text-right font-medium px-4 py-3">Year-1 from</th>
                            <th className="text-left font-medium px-4 py-3">Valid until</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map(p => {
                            const minY1 = Math.min(...(p.options || []).map(o => o?.computed?.year_one_total_net || Infinity));
                            return (
                                <tr key={p.id} onClick={() => navigate(`/proposals/${p.id}`)}
                                    className="border-t border-[color:var(--bm-border)] hover:bg-[color:var(--bm-bg-mute)] cursor-pointer"
                                    data-testid={`client-proposal-row-${p.id}`}>
                                    <td className="px-5 py-3">
                                        <div className="font-medium text-[color:var(--bm-text)]">{p.title}</div>
                                        <div className="text-[11.5px] text-[color:var(--bm-text-subtle)]">{p.proposal_number}</div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`bm-pill ${PROPOSAL_STATUS[p.status]?.tone || ''} border`}>
                                            {PROPOSAL_STATUS[p.status]?.label || p.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-right font-medium">{minY1 === Infinity ? '—' : GBP(minY1)}</td>
                                    <td className="px-4 py-3 text-[color:var(--bm-text-muted)]">{p.valid_until || '—'}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
}



// ── Delivery & technical info tab ─────────────────────────────────────────────

const DELIVERY_FIELDS = [
    { key: 'platform_url',        label: 'Client platform URL',         placeholder: 'https://ops.dmdb.uk' },
    { key: 'admin_login_url',     label: 'Admin login URL',             placeholder: 'https://hq.beyondmeasure.global' },
    { key: 'azure_tenant_id',     label: 'Azure Tenant ID' },
    { key: 'azure_client_id',     label: 'Azure Client ID' },
    { key: 'azure_client_secret', label: 'Azure Client Secret', secret: true },
    { key: 'resend_domain',       label: 'Resend domain / sending address', placeholder: 'noreply@beyondmeasure.global' },
    { key: 'dns_provider',        label: 'DNS provider',                placeholder: 'Cloudflare, GoDaddy …' },
    { key: 'db_name',             label: 'Mongo collection / db name' },
    { key: 'github_ref',          label: 'GitHub branch / workspace' },
];

function DeliveryTab({ client, onSave, saving }) {
    const [editing, setEditing] = useState(false);
    const [form, setForm]       = useState(client.delivery_info || {});
    const [showSecret, setShowSecret] = useState(false);

    useEffect(() => { setForm(client.delivery_info || {}); }, [client]);

    const set = (k, v) => setForm(s => ({ ...s, [k]: v }));

    const save = async () => {
        await onSave({ delivery_info: form });
        setEditing(false);
    };

    const renderField = (f) => {
        const { key, label, placeholder, secret } = f;
        const v = form[key] || '';
        return (
            <div key={key}>
                <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">{label}</Label>
                {editing ? (
                    <div className="relative mt-1.5">
                        <Input
                            type={secret && !showSecret ? 'password' : 'text'}
                            value={v}
                            onChange={e => set(key, e.target.value)}
                            placeholder={placeholder}
                            className="h-10 pr-10"
                            data-testid={`delivery-input-${key}`}
                        />
                        {secret && (
                            <button type="button"
                                onClick={() => setShowSecret(s => !s)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-[color:var(--bm-text-muted)] hover:text-[color:var(--bm-text)]">
                                {showSecret ? <EyeOff size={15} /> : <Eye size={15} />}
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="mt-1 min-h-[40px] py-2 px-3 rounded-md bg-[color:var(--bm-bg-mute)] text-[13px] flex items-center justify-between">
                        {v ? (
                            <span className="font-mono text-[12.5px] truncate">{secret && !showSecret ? '•'.repeat(Math.min(v.length, 12)) : v}</span>
                        ) : (
                            <span className="text-[color:var(--bm-text-subtle)] italic">Not set</span>
                        )}
                        {secret && v && (
                            <button onClick={() => setShowSecret(s => !s)}
                                className="ml-2 text-[color:var(--bm-text-muted)] hover:text-[color:var(--bm-text)]">
                                {showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                            </button>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6" data-testid="delivery-tab">
            <div className="flex items-start justify-between mb-5">
                <div>
                    <h3 className="font-display text-[16px] font-semibold">Delivery & technical info</h3>
                    <p className="text-[12.5px] text-[color:var(--bm-text-muted)] mt-0.5">Operational setup details for this tenant — auth, DNS, deployment.</p>
                </div>
                {editing ? (
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={() => { setEditing(false); setForm(client.delivery_info || {}); }} className="h-9">Cancel</Button>
                        <Button onClick={save} disabled={saving} className="h-9" style={{ background: 'var(--bm-primary)' }} data-testid="delivery-save-btn">
                            <Save size={14} className="mr-1.5" /> Save
                        </Button>
                    </div>
                ) : (
                    <Button variant="outline" onClick={() => setEditing(true)} className="h-9" data-testid="delivery-edit-btn">
                        <Pencil size={14} className="mr-1.5" /> Edit
                    </Button>
                )}
            </div>

            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
                {DELIVERY_FIELDS.map(renderField)}
                <div>
                    <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Deployment environment</Label>
                    {editing ? (
                        <Select value={form.deployment_env || ''} onValueChange={v => set('deployment_env', v)}>
                            <SelectTrigger className="mt-1.5 h-10" data-testid="delivery-env-select"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="preview">Preview</SelectItem>
                                <SelectItem value="live">Live</SelectItem>
                                <SelectItem value="both">Both</SelectItem>
                            </SelectContent>
                        </Select>
                    ) : (
                        <div className="mt-1 min-h-[40px] py-2 px-3 rounded-md bg-[color:var(--bm-bg-mute)] text-[13px] capitalize">
                            {form.deployment_env || <span className="text-[color:var(--bm-text-subtle)] italic">Not set</span>}
                        </div>
                    )}
                </div>
                <div className="sm:col-span-2">
                    <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Internal notes</Label>
                    {editing ? (
                        <Textarea value={form.internal_notes || ''} onChange={e => set('internal_notes', e.target.value)} className="mt-1.5 min-h-[100px]" data-testid="delivery-notes" />
                    ) : (
                        <div className="mt-1 min-h-[60px] py-2 px-3 rounded-md bg-[color:var(--bm-bg-mute)] text-[13px] whitespace-pre-wrap">
                            {form.internal_notes || <span className="text-[color:var(--bm-text-subtle)] italic">No notes</span>}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
